import { useQueryClient, useSuspenseQuery } from '@tanstack/react-query'
import { sortBy } from 'es-toolkit'
import { DataTableSortStatus } from 'mantine-datatable'
import { useDeferredValue, useEffect, useState } from 'react'
import { useShallow } from 'zustand/react/shallow'

import type { CoinItemInGetList, CoinItemInPostList } from '@/services/coin/coins'
import { coinsQueryOptions, useCoinsByIds } from '@/services/coin/coins'
import type { TokenizedStockItem } from '@/services/coin/tokenizedStocks'
import type { CoinId, PopularCoin, TopMoverItem } from '@/types/coin'

import {
  COINS_OG_PAGE_SIZE,
  COINS_PAGE_SIZE,
  CoinTab,
  TOKENIZED_STOCKS_PAGE_SIZE,
} from '../../configs'
import { useCoinsTableStore } from '../../context'
import type { TableRecord } from '../columns/configs'

//Build query options with pageSize override
const buildQueryOptions = (tab: any, page: number, pageSize: number) => {
  if (tab.value === CoinTab.AllCoins) {
    return coinsQueryOptions({ page, limit: pageSize })
  }
  return tab.getQueryOptions ? tab.getQueryOptions(page) : tab.queryOptions
}

// Get the ideal table state to avoid hiding the table when the query is suspending
function useIdealTableState(pageSize: number) {
  const { currentCoinTab, currentPage } = useCoinsTableStore(
    useShallow(({ currentCoinTab, currentPage }) => ({
      currentCoinTab,
      currentPage,
    })),
  )

  const currentTabQueryOptions = buildQueryOptions(currentCoinTab, currentPage, pageSize)

  // Defer the values re-render to avoid triggering suspense fallback
  const deferredCoinTab = useDeferredValue(currentCoinTab)
  const deferredPage = useDeferredValue(currentPage)

  const deferredTabQueryOptions = buildQueryOptions(deferredCoinTab, deferredPage, pageSize)

  const queryClient = useQueryClient()
  const queryState = queryClient.getQueryState(currentTabQueryOptions.queryKey)
  const isPending = !queryState?.data

  if (isPending) {
    return {
      coinTab: deferredCoinTab,
      tabQueryOptions: deferredTabQueryOptions,
      currentPage: deferredPage,
      isPending,
    }
  }

  return {
    coinTab: currentCoinTab,
    tabQueryOptions: currentTabQueryOptions,
    currentPage,
    isPending,
  }
}

export default function useTableData({ isOGPrice }: { isOGPrice?: boolean }) {
  const pageSize = isOGPrice ? COINS_OG_PAGE_SIZE : COINS_PAGE_SIZE

  const { coinTab, tabQueryOptions, currentPage, isPending } = useIdealTableState(pageSize)

  const { data } = useSuspenseQuery(tabQueryOptions)

  let totalRecords = 0
  let coins:
    | CoinItemInGetList[]
    | TopMoverItem[]
    | PopularCoin[]
    | CoinItemInPostList[]
    | TokenizedStockItem[] = []

  // Handle different response structures based on tab type
  if ([CoinTab.AllCoins, CoinTab.New].includes(coinTab.value)) {
    // @ts-expect-error Complex mixed query options that useSuspenseQuery can't handle
    coins = data.data
    // @ts-expect-error Complex mixed query options that useSuspenseQuery can't handle
    totalRecords = data.total

    // Only show 1 page of new coins (your current behavior)
    if (coinTab.value === CoinTab.New) {
      totalRecords = coins.length
    }
  } else if ([CoinTab.TopGainers, CoinTab.TopLosers].includes(coinTab.value)) {
    coins = data as unknown as TopMoverItem[]
    totalRecords = coins.length
  } else if ([CoinTab.MostPopular, CoinTab.Favorites].includes(coinTab.value)) {
    coins = data as unknown as PopularCoin[]
    totalRecords = coins.length
  } else if (coinTab.value === CoinTab.TokenizedStocks) {
    coins = data as unknown as TokenizedStockItem[]
    totalRecords = coins.length
  }

  // Tabs other than AllCoins and New need another query to get batch coins by ids
  const isTabNeedsAnotherQuery = [
    CoinTab.TopGainers,
    CoinTab.TopLosers,
    CoinTab.MostPopular,
    CoinTab.Favorites,
    CoinTab.TokenizedStocks,
  ].includes(coinTab.value)

  // Apply client paging for list-tabs BEFORE mapping ids
  let pagedCoins = coins
  if (isOGPrice && isTabNeedsAnotherQuery) {
    const start = (currentPage - 1) * pageSize
    const end = start + pageSize
    pagedCoins = (coins as any[]).slice(start, end)
  } else if (coinTab.value === CoinTab.TokenizedStocks) {
    const start = (currentPage - 1) * TOKENIZED_STOCKS_PAGE_SIZE
    const end = start + TOKENIZED_STOCKS_PAGE_SIZE
    pagedCoins = (coins as any[]).slice(start, end)
  }

  let coinIds: CoinId[] = []
  if (isTabNeedsAnotherQuery) {
    coinIds = (pagedCoins as any[]).map((coin: any) => coin[coinTab.coinIdKey] as CoinId)
  }

  const { data: batchCoins } = useCoinsByIds(coinIds)
  if (isTabNeedsAnotherQuery) {
    coins = batchCoins
  }

  const finalCoins = coins as CoinItemInGetList[] | CoinItemInPostList[]

  const [records, setRecords] = useState(finalCoins)
  const [sortStatus, setSortStatus] = useState<DataTableSortStatus<TableRecord> | undefined>()

  function handleSortStatusChange(newSortStatus: DataTableSortStatus<TableRecord>) {
    setSortStatus({
      ...newSortStatus,
      direction: newSortStatus.sortKey !== sortStatus?.sortKey ? 'desc' : newSortStatus.direction,
    })
  }

  useEffect(() => {
    if (!sortStatus) {
      setRecords(finalCoins)
    } else {
      // @ts-expect-error Hybrid coins type
      const sortedCoins = sortBy(finalCoins, [sortStatus.sortKey])
      setRecords(sortStatus.direction === 'desc' ? sortedCoins.reverse() : sortedCoins)
    }
  }, [sortStatus, finalCoins])

  return {
    isPending,
    records,
    totalRecords, // keep full total so pagination count is correct
    sortStatus,
    handleSortStatusChange,
  }
}
